"use client"
import { CENTRA_MARKET_CODE, CentraMarketCode } from "@/data/supported-markets"
import logger from "@/lib/logger/logger-init"
import { getMarketCodeFromCentraLocale } from "@/app/(site)/_utils/misc"
import React, { useEffect, useState } from "react"
import { GladlyScript } from "@/lib/scripts/Gladly/GladlyScript"
import { usePathname } from "next/navigation"
// Import your consent logic
import useOneTrustOptInLoad from "@/app/(site)/_utils/_hooks/useOneTrustOptInLoad"

/*
 *   @TODO There are some issues with this implementation, mostly due to Gladly not being able to swap "app"
 *   So whenever one swap site, we actually reload the script and give it a new global property name.
 *   This is not optimal and should be fixed in the future since it creates a new script tag every time.
 */

// These are the available events that can be listened to
const GladlyAppEvents = [
    "availability:change",
    "conversation:ended",
    "conversation:started",
    "campaign:triggered",
    "customer:onboarded",
    "message:received",
    "message:sent",
    "quick-action:selected",
    "search:result-selected",
    "sidekick:closed",
    "sidekick:opened"
]

// These are the (currently) set up apps in Gladly
const gladlyApps = [
    "pocsports.com-APAC",
    "pocsports.com-EU",
    "pocsports.com-NorAm",
    "pocsports.com",
    "pocsports.com-test"
] as const

export type GladlyApps = (typeof gladlyApps)[number]

export type GladlyEnvironments = "PROD" | "STAGING"

// We do a config mapping for each environment and market code to use later
const gladlyConfiguredApps: Record<
    GladlyEnvironments,
    Record<CentraMarketCode | "default", GladlyApps>
> = {
    PROD: {
        [CENTRA_MARKET_CODE.Jp]: "pocsports.com-APAC",
        [CENTRA_MARKET_CODE.Eu]: "pocsports.com-EU",
        [CENTRA_MARKET_CODE.Na]: "pocsports.com-NorAm",
        [CENTRA_MARKET_CODE.Apac]: "pocsports.com-APAC",
        default: "pocsports.com"
    },
    STAGING: {
        [CENTRA_MARKET_CODE.Jp]: "pocsports.com",
        [CENTRA_MARKET_CODE.Eu]: "pocsports.com",
        [CENTRA_MARKET_CODE.Na]: "pocsports.com",
        [CENTRA_MARKET_CODE.Apac]: "pocsports.com",
        default: "pocsports.com"
    }
} as const

export type GladlyConfiguredApps = typeof gladlyConfiguredApps

export type GladlyProps = {
    params: { locale: string }
}

export const Gladly: React.FC<GladlyProps> = ({ params }) => {
    // For debugging purposes
    const FORCE_PRODUCTION_APPLICATIONS = false

    // We use this to keep track on if the script has loaded or not
    const [hasGladlyLoaded, setGladlyLoaded] = useState(false)

    // Pathname is imported to be able to do "navigate()" in the chat widget for context purposes.
    const pathname = usePathname()

    // Checks the env.
    const environment: GladlyEnvironments =
        process.env.NODE_ENV === "production" || FORCE_PRODUCTION_APPLICATIONS
            ? "PROD"
            : "STAGING"

    // Get the appId from the config
    const appId =
        gladlyConfiguredApps[environment][
            getMarketCodeFromCentraLocale(params.locale as string) ?? "default"
        ]

    // The "script
    const scriptName = `${environment}-${appId}`

    // We create this variable to hold the propertyName since it is used on a couple of places.
    const currentGladlyWindowPropertyName = "Gladly-" + scriptName

    // check if the user has given consent to the functional cookies
    const consent = useOneTrustOptInLoad()

    // Setup a new window object to keep track of the Gladly scripts for debugging purposes
    useEffect(() => {
        if (!window.poc) {
            window.poc = {}
        }
        if (!window.poc.gladly) {
            window.poc.gladly = {}
        }
    }, [])

    // This useEffect is used to trigger a "navigate" function in Gladly to give context on user navigation
    useEffect(() => {
        if (!hasGladlyLoaded || !window[currentGladlyWindowPropertyName]) {
            return
        }
        window[currentGladlyWindowPropertyName]?.navigate?.()
        // Sometimes the widget closes and is not displayed anymore so we show it and close it again to display the widget icon
        window[currentGladlyWindowPropertyName]?.show?.()
        window[currentGladlyWindowPropertyName]?.close?.()
    }, [pathname])

    // This useEffect is used to initialize the Gladly script and update whenever something changes (like a site swap)
    useEffect(() => {
        if (!hasGladlyLoaded || !window[currentGladlyWindowPropertyName]) {
            return
        }

        // We cannot use the auto-config since we need to be able to swap the appId on site swap and it doesn't do that on SPAs
        window[currentGladlyWindowPropertyName]
            .init({
                appId: appId
            })
            .then(() => {
                window["poc"]["gladly"][scriptName] =
                    window[currentGladlyWindowPropertyName]
                // The widget is not displayed from start if we init manually so we show it and close it again to display the widget icon
                window[currentGladlyWindowPropertyName].show()
                window[currentGladlyWindowPropertyName].close()
            })
            .catch(function (error: string) {
                // If anything goes wrong when Glad App is being initialized, this gets called.
                logger.error("Gladly init error:" + error, {
                    scriptName
                })
            })

        return () => {
            // Remove or unmount the widgets etc if something changes.
            window[currentGladlyWindowPropertyName]?.close?.()
            delete window[currentGladlyWindowPropertyName]
            delete window.poc.gladly[scriptName]
            document.getElementById(`gladly-script-${appId}`)?.remove()
            document.getElementById(`gladlyChat_container`)?.remove()
            setGladlyLoaded(false)
        }
    }, [hasGladlyLoaded, appId, environment, scriptName])

    // If we are on the server, skip rendering the widget.
    return typeof window !== "undefined" && consent?.functionalConsent ? (
        <GladlyScript
            variableName={currentGladlyWindowPropertyName}
            environment={environment}
            appId={appId}
            readyCallback={() => setGladlyLoaded(true)}
            errorCallback={(error: string) => {
                logger.error("Gladly Script could not be loaded.", {
                    error
                })
            }}
        />
    ) : null
}
